import axios, { CancelTokenSource } from 'axios';
import { getBackendURLFromHost } from '@lendica/utils';

const baseURL = getBackendURLFromHost(window!.location.hostname);
const axiosInstance = axios.create({ baseURL });

export interface S3UploadConfig {
    presignedUrl: string;
    presignedUrlParams?: Record<string, any>;
    getObjectKey?: (fileName: string) => string;
}

export interface S3PresignedResponse {
    url: string;
    fields: { [key: string]: string };
}

export interface S3UploadProgress {
    loaded: number;
    total: number;
    progress: number; // 0-100
}

/**
 * Count pages in a PDF file
 */
export async function countPdfPages(file: File): Promise<number> {
    return new Promise(resolve => {
        const reader = new FileReader();
        reader.readAsBinaryString(file);
        reader.onloadend = () => {
            const result = reader?.result;
            if (typeof result !== 'string') {
                resolve(1);
                return;
            }
            const numOfPages = result?.match(/\/Type[\s]*\/Page[^s]/g)?.length ?? 1;
            resolve(numOfPages);
        };
    });
}

/**
 * Get presigned URL for S3 upload
 */
export async function getPresignedUrl(
    file: File & { objectKey: string },
    numOfPages: number,
    config: S3UploadConfig,
    cancelToken: CancelTokenSource
): Promise<S3PresignedResponse> {
    const response = await axiosInstance.post<S3PresignedResponse>(
        config.presignedUrl,
        {
            object_key: file.objectKey,
            object_name: file.name,
            bucket_name: 'lendica-sandbox',
            fields: {
                'Content-Type': file.type,
                'x-amz-meta-numpages': numOfPages,
            },
            conditions: [
                ['starts-with', '$Content-Type', ''],
                ['starts-with', '$x-amz-meta-numpages', ''],
            ],
            ...config.presignedUrlParams,
        },
        {
            cancelToken: cancelToken.token,
        }
    );

    return response.data;
}

/**
 * Upload file to S3 using presigned URL
 */
export async function uploadToS3(
    file: File,
    s3Presigned: S3PresignedResponse,
    cancelToken: CancelTokenSource,
    onProgress?: (progress: S3UploadProgress) => void
): Promise<void> {
    const formData = new FormData();

    // Add S3 fields
    for (const [key, value] of Object.entries(s3Presigned.fields)) {
        formData.append(key, value);
    }
    formData.append('file', file);

    // Upload with progress tracking
    await axiosInstance.post(s3Presigned.url, formData, {
        onUploadProgress: ({ loaded, total }) => {
            if (total && onProgress) {
                const progress = Math.round((loaded / total) * 100);
                onProgress({ loaded, total, progress });
            }
        },
        cancelToken: cancelToken.token,
    });
}

/**
 * Complete S3 upload flow: count pages (if PDF), get presigned URL, upload file
 */
export async function uploadFileToS3(
    file: File,
    config: S3UploadConfig,
    cancelToken: CancelTokenSource,
    onProgress?: (progress: S3UploadProgress) => void
): Promise<string> {
    // Add object key to file
    const fileWithKey = Object.assign(file, {
        objectKey: config.getObjectKey ? config.getObjectKey(file.name) : file.name,
    });

    // Count PDF pages if needed
    let numOfPages = 1;
    if (file.type === 'application/pdf') {
        numOfPages = await countPdfPages(file);
    }

    // Get presigned URL
    const s3Presigned = await getPresignedUrl(fileWithKey, numOfPages, config, cancelToken);

    // Upload to S3
    await uploadToS3(file, s3Presigned, cancelToken, onProgress);

    // Return S3 key
    return s3Presigned.fields.key;
}
